import { Fragment, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ContractorUpdateInput,
  EntryNoticeDTO,
  EntryNoticeEmailRecipientDTO,
  EntryNoticePurpose,
  ShortNoticeReason,
  WorkOrderAdminCreateInput,
  WorkOrderCategory,
  WorkOrderDTO,
  WorkOrderPriority,
  WorkOrderScheduleInput,
  WorkOrderScheduleResponse,
  WorkOrderCostReviewDTO,
  ScheduleProposalReviewDTO,
} from '@propmgt/shared';
import { useAuth } from '../../providers/AuthProvider';
import { CONTRACTORS_KEY } from './Contractors';
import { api, ApiError, errorMessage } from '../../api/client';
import { formatDate, formatDateTime } from '../../lib/date';
import { formatCents } from '../../lib/money';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { Dialog } from '../../components/a11y/Dialog';

/**
 * Work orders — the owner/staff maintenance queue + lifecycle (mirrors the Tenants.tsx canonical
 * pattern). A react-query list + a "New work order" Dialog (property→unit picker), per-row expand
 * to a detail area (history, entry notices, and status-appropriate action forms). Every write goes
 * through the shared `api` client (CSRF handled there) and invalidates the list + the row detail.
 */

const WORK_ORDERS_KEY = ['admin', 'work-orders'] as const;
const detailKey = (id: string) => ['admin', 'work-orders', id] as const;
const CONTRACTOR_KEY = ['admin', 'org', 'contractor'] as const;
const PENDING_COSTS_KEY = ['admin', 'work-order-costs', 'submitted'] as const;
const costsKey = (id: string) => ['admin', 'work-orders', id, 'costs'] as const;
const PENDING_PROPOSALS_KEY = ['admin', 'schedule-proposals', 'proposed'] as const;
const proposalsKey = (id: string) => ['admin', 'work-orders', id, 'proposals'] as const;

const CATEGORIES: WorkOrderCategory[] = [
  'plumbing',
  'electrical',
  'hvac',
  'appliance',
  'structural',
  'pest',
  'landscaping',
  'safety',
  'general',
];
const PRIORITIES: WorkOrderPriority[] = ['emergency', 'urgent', 'routine', 'low'];
const PURPOSES: EntryNoticePurpose[] = ['repair', 'inspection', 'showing', 'emergency'];
const SHORT_NOTICE_REASONS: ShortNoticeReason[] = ['emergency', 'tenant_consented', 'other'];

/** Empty → undefined, so optional string fields are omitted rather than sent as "". */
function opt(v: string): string | undefined {
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}

/** Dollars string → integer cents, or null when blank/NaN (money is stored in cents). */
function dollarsToCents(v: string): number | null {
  const t = v.trim();
  if (t.length === 0) return null;
  const n = Math.round(parseFloat(t) * 100);
  return Number.isFinite(n) ? n : null;
}

/* ------------------------------------------------------------------ *
 * Create — "New work order" (property→unit picker).                  *
 * ------------------------------------------------------------------ */

function WorkOrderForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [propertyId, setPropertyId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [category, setCategory] = useState<WorkOrderCategory>('general');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<WorkOrderPriority>('routine');
  const [permissionToEnter, setPermissionToEnter] = useState(false);
  const [availability, setAvailability] = useState('');

  const properties = useQuery({
    queryKey: ['admin', 'properties'],
    queryFn: () => api.properties.list(),
  });
  const units = useQuery({
    queryKey: ['admin', 'units', propertyId],
    queryFn: () => api.units.list(propertyId),
    enabled: propertyId !== '',
  });

  const save = useMutation({
    mutationFn: () => {
      const body: WorkOrderAdminCreateInput = {
        unitId,
        category,
        title: title.trim(),
        description: description.trim(),
        priority,
        permissionToEnter,
        availability: opt(availability) ?? null,
      };
      return api.workOrders.create(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: WORK_ORDERS_KEY });
      onClose();
    },
  });

  return (
    <Dialog title="New work order" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}

        <div>
          <label htmlFor="wo-property">Property</label>
          <select
            id="wo-property"
            value={propertyId}
            required
            onChange={(e) => {
              setPropertyId(e.target.value);
              setUnitId('');
            }}
          >
            <option value="">Select a property…</option>
            {properties.data?.properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {p.city}, {p.state}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="wo-unit">Unit</label>
          <select
            id="wo-unit"
            value={unitId}
            required
            disabled={propertyId === '' || units.isLoading}
            onChange={(e) => setUnitId(e.target.value)}
          >
            <option value="">
              {propertyId === '' ? 'Select a property first' : units.isLoading ? 'Loading units…' : 'Select a unit…'}
            </option>
            {units.data?.units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="wo-category">Category</label>
          <select
            id="wo-category"
            value={category}
            onChange={(e) => setCategory(e.target.value as WorkOrderCategory)}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="wo-title">Title</label>
          <input
            id="wo-title"
            value={title}
            required
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Short summary (e.g. Leaking kitchen faucet)"
          />
        </div>

        <div>
          <label htmlFor="wo-desc">Description</label>
          <textarea
            id="wo-desc"
            value={description}
            required
            rows={4}
            maxLength={4000}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the problem in detail."
          />
        </div>

        <div>
          <label htmlFor="wo-priority">Priority</label>
          <select
            id="wo-priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as WorkOrderPriority)}
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div className="row">
          <input
            id="wo-permission"
            type="checkbox"
            style={{ width: 'auto' }}
            checked={permissionToEnter}
            onChange={(e) => setPermissionToEnter(e.target.checked)}
          />
          <label htmlFor="wo-permission" style={{ margin: 0 }}>
            Tenant has granted permission to enter
          </label>
        </div>

        <div>
          <label htmlFor="wo-avail">Availability (optional)</label>
          <input
            id="wo-avail"
            value={availability}
            maxLength={500}
            onChange={(e) => setAvailability(e.target.value)}
            placeholder="e.g. Weekday mornings before 11am"
          />
        </div>

        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Create work order'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Per-status action forms (each its own small mutation).             *
 * ------------------------------------------------------------------ */

/** Shared invalidation after any lifecycle write — refresh the queue + this row's detail. */
function useInvalidateWorkOrder(id: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: WORK_ORDERS_KEY });
    void queryClient.invalidateQueries({ queryKey: detailKey(id) });
  };
}

function TriageAction({ id }: { id: string }) {
  const invalidate = useInvalidateWorkOrder(id);
  const [note, setNote] = useState('');
  const m = useMutation({
    mutationFn: () => api.workOrders.triage(id, opt(note)),
    onSuccess: invalidate,
  });
  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <h4 style={{ margin: 0 }}>Triage</h4>
      {m.isError && <StatusMessage tone="err">{errorMessage(m.error)}</StatusMessage>}
      <div>
        <label htmlFor={`triage-note-${id}`}>Note (optional)</label>
        <input
          id={`triage-note-${id}`}
          value={note}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <div>
        <button type="submit" disabled={m.isPending}>
          {m.isPending ? 'Working…' : 'Mark triaged'}
        </button>
      </div>
    </form>
  );
}

function AssignAction({ id }: { id: string }) {
  const invalidate = useInvalidateWorkOrder(id);
  const [assignedTo, setAssignedTo] = useState('');
  const [note, setNote] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api.workOrders.assign(id, { assignedTo: assignedTo.trim(), note: opt(note) ?? null }),
    onSuccess: invalidate,
  });
  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <h4 style={{ margin: 0 }}>Assign</h4>
      {m.isError && <StatusMessage tone="err">{errorMessage(m.error)}</StatusMessage>}
      <div>
        <label htmlFor={`assign-to-${id}`}>Assign to</label>
        <input
          id={`assign-to-${id}`}
          value={assignedTo}
          required
          maxLength={200}
          onChange={(e) => setAssignedTo(e.target.value)}
          placeholder="Vendor or staff name"
        />
      </div>
      <div>
        <label htmlFor={`assign-note-${id}`}>Note (optional)</label>
        <input
          id={`assign-note-${id}`}
          value={note}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <div>
        <button type="submit" disabled={m.isPending}>
          {m.isPending ? 'Working…' : 'Assign'}
        </button>
      </div>
    </form>
  );
}

function ScheduleAction({ id }: { id: string }) {
  const invalidate = useInvalidateWorkOrder(id);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [purpose, setPurpose] = useState<EntryNoticePurpose>('repair');
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState<ShortNoticeReason>('emergency');
  const [assignedTo, setAssignedTo] = useState('');
  const [note, setNote] = useState('');
  const [notice, setNotice] = useState<WorkOrderScheduleResponse | null>(null);

  const m = useMutation({
    mutationFn: () => {
      // datetime-local yields an offset-less string; the API requires an explicit offset.
      const body: WorkOrderScheduleInput = {
        scheduledEntryStart: new Date(start).toISOString(),
        scheduledEntryEnd: end !== '' ? new Date(end).toISOString() : null,
        purpose,
        overrideShortNotice: override,
        shortNoticeReason: override ? reason : null,
        assignedTo: opt(assignedTo) ?? null,
        note: opt(note) ?? null,
      };
      return api.workOrders.schedule(id, body);
    },
    onSuccess: (resp) => {
      setNotice(resp);
      invalidate();
    },
  });

  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <h4 style={{ margin: 0 }}>Schedule entry (§1954)</h4>
      {m.isError && <StatusMessage tone="err">{errorMessage(m.error)}</StatusMessage>}
      {notice && (
        <>
          <StatusMessage tone="ok">
            Entry notice served (posted to the tenant’s portal).{' '}
            <button type="button" className="secondary" onClick={() => window.open(notice.noticeUrl)}>
              Download notice
            </button>
          </StatusMessage>
          <div className="muted">
            Email copies:
            <NoticeEmailCopies notice={notice.entryNotice} />
          </div>
        </>
      )}
      <div>
        <label htmlFor={`sched-start-${id}`}>Entry start</label>
        <input
          id={`sched-start-${id}`}
          type="datetime-local"
          value={start}
          required
          onChange={(e) => setStart(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor={`sched-end-${id}`}>Entry end (optional)</label>
        <input
          id={`sched-end-${id}`}
          type="datetime-local"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor={`sched-purpose-${id}`}>Purpose</label>
        <select
          id={`sched-purpose-${id}`}
          value={purpose}
          onChange={(e) => setPurpose(e.target.value as EntryNoticePurpose)}
        >
          {PURPOSES.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>
      <div className="row">
        <input
          id={`sched-override-${id}`}
          type="checkbox"
          style={{ width: 'auto' }}
          checked={override}
          onChange={(e) => setOverride(e.target.checked)}
        />
        <label htmlFor={`sched-override-${id}`} style={{ margin: 0 }}>
          Override short notice (&lt;24h lead)
        </label>
      </div>
      {override && (
        <div>
          <label htmlFor={`sched-reason-${id}`}>Short-notice reason</label>
          <select
            id={`sched-reason-${id}`}
            value={reason}
            required
            onChange={(e) => setReason(e.target.value as ShortNoticeReason)}
          >
            {SHORT_NOTICE_REASONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label htmlFor={`sched-assign-${id}`}>Assign to (optional)</label>
        <input
          id={`sched-assign-${id}`}
          value={assignedTo}
          maxLength={200}
          onChange={(e) => setAssignedTo(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor={`sched-note-${id}`}>Note (optional)</label>
        <input
          id={`sched-note-${id}`}
          value={note}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <div>
        <button type="submit" disabled={m.isPending}>
          {m.isPending ? 'Working…' : 'Schedule + serve notice'}
        </button>
      </div>
    </form>
  );
}

function ResolveAction({ id }: { id: string }) {
  const invalidate = useInvalidateWorkOrder(id);
  const [resolutionNote, setResolutionNote] = useState('');
  const [cost, setCost] = useState('');
  const [note, setNote] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api.workOrders.resolve(id, {
        resolutionNote: opt(resolutionNote) ?? null,
        resolutionCostCents: dollarsToCents(cost),
        note: opt(note) ?? null,
      }),
    onSuccess: invalidate,
  });
  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <h4 style={{ margin: 0 }}>Resolve</h4>
      {m.isError && <StatusMessage tone="err">{errorMessage(m.error)}</StatusMessage>}
      <div>
        <label htmlFor={`resolve-note-${id}`}>Resolution note (optional)</label>
        <textarea
          id={`resolve-note-${id}`}
          value={resolutionNote}
          rows={2}
          maxLength={4000}
          onChange={(e) => setResolutionNote(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor={`resolve-cost-${id}`}>Your cost (optional, USD — approved contractor costs are added)</label>
        <input
          id={`resolve-cost-${id}`}
          type="number"
          min="0"
          step="0.01"
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          placeholder="0.00"
        />
      </div>
      <div>
        <label htmlFor={`resolve-internal-${id}`}>Internal note (optional)</label>
        <input
          id={`resolve-internal-${id}`}
          value={note}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <div>
        <button type="submit" disabled={m.isPending}>
          {m.isPending ? 'Working…' : 'Mark resolved'}
        </button>
      </div>
    </form>
  );
}

function CloseAction({ id }: { id: string }) {
  const invalidate = useInvalidateWorkOrder(id);
  const m = useMutation({
    mutationFn: () => api.workOrders.close(id),
    onSuccess: invalidate,
  });
  return (
    <div className="card stack">
      <h4 style={{ margin: 0 }}>Close</h4>
      {m.isError && <StatusMessage tone="err">{errorMessage(m.error)}</StatusMessage>}
      <div>
        <button type="button" disabled={m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? 'Working…' : 'Close work order'}
        </button>
      </div>
    </div>
  );
}

function CancelAction({ id }: { id: string }) {
  const invalidate = useInvalidateWorkOrder(id);
  const [cancelledReason, setCancelledReason] = useState('');
  const m = useMutation({
    mutationFn: () => api.workOrders.cancel(id, { cancelledReason: opt(cancelledReason) ?? null }),
    onSuccess: invalidate,
  });
  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (window.confirm('Cancel this work order?')) m.mutate();
      }}
    >
      <h4 style={{ margin: 0 }}>Cancel</h4>
      {m.isError && <StatusMessage tone="err">{errorMessage(m.error)}</StatusMessage>}
      <div>
        <label htmlFor={`cancel-reason-${id}`}>Reason (optional)</label>
        <input
          id={`cancel-reason-${id}`}
          value={cancelledReason}
          maxLength={500}
          onChange={(e) => setCancelledReason(e.target.value)}
        />
      </div>
      <div>
        <button type="submit" className="secondary" disabled={m.isPending}>
          {m.isPending ? 'Working…' : 'Cancel work order'}
        </button>
      </div>
    </form>
  );
}

function NoteAction({ id }: { id: string }) {
  const invalidate = useInvalidateWorkOrder(id);
  const [note, setNote] = useState('');
  const m = useMutation({
    mutationFn: () => api.workOrders.note(id, { note: note.trim() }),
    onSuccess: () => {
      setNote('');
      invalidate();
    },
  });
  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <h4 style={{ margin: 0 }}>Add internal note</h4>
      {m.isError && <StatusMessage tone="err">{errorMessage(m.error)}</StatusMessage>}
      <div>
        <label htmlFor={`note-${id}`}>Note (landlord-internal)</label>
        <input
          id={`note-${id}`}
          value={note}
          required
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <div>
        <button type="submit" disabled={m.isPending}>
          {m.isPending ? 'Working…' : 'Add note'}
        </button>
      </div>
    </form>
  );
}

/** Email the job to the maintenance contractor (NOT a status transition). Defaults to the org's
 *  configured contractor list; an optional address overrides it for a one-off recipient. */
function DispatchAction({ id, canAssign }: { id: string; canAssign: boolean }) {
  const invalidate = useInvalidateWorkOrder(id);
  const contractor = useQuery({ queryKey: CONTRACTOR_KEY, queryFn: () => api.org.getContractor() });
  const directory = useQuery({ queryKey: CONTRACTORS_KEY, queryFn: () => api.contractors.list() });
  const [targetId, setTargetId] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [note, setNote] = useState('');
  const [markAssigned, setMarkAssigned] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () =>
      api.workOrders.dispatch(id, {
        toEmail: targetId ? null : (opt(toEmail) ?? null),
        contractorId: targetId || null,
        note: opt(note) ?? null,
        markAssigned: markAssigned && canAssign,
      }),
    onSuccess: (resp) => {
      setSentTo(resp.workOrder.dispatchedToEmail ?? null);
      setNote('');
      invalidate();
    },
  });

  const defaults = contractor.data?.emails ?? [];
  const hasDefault = defaults.length > 0;
  const activeDirectory = (directory.data?.contractors ?? []).filter((c) => c.active && c.emails.length > 0);
  const target = activeDirectory.find((c) => c.id === targetId);
  const canSend = Boolean(target) || hasDefault || opt(toEmail) !== undefined;

  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <h4 style={{ margin: 0 }}>Dispatch to contractor</h4>
      {m.isError && <StatusMessage tone="err">{errorMessage(m.error)}</StatusMessage>}
      {sentTo && <StatusMessage tone="ok">Emailed to {sentTo}.</StatusMessage>}
      {activeDirectory.length > 0 && (
        <div>
          <label htmlFor={`dispatch-target-${id}`}>Contractor</label>
          <select id={`dispatch-target-${id}`} value={targetId} onChange={(e) => setTargetId(e.target.value)}>
            <option value="">Default contractor list</option>
            {activeDirectory.map((c) => (
              <option key={c.id} value={c.id}>
                {c.companyName}
              </option>
            ))}
          </select>
        </div>
      )}
      <p className="muted" style={{ margin: 0 }}>
        {target
          ? `Sends to ${target.emails.join(', ')} (${target.companyName}).`
          : hasDefault
            ? `Sends to ${defaults.join(', ')}${contractor.data?.name ? ` (${contractor.data.name})` : ''}.`
            : 'No contractor is configured yet — set one from the “Contractor” button above, or enter an address below.'}
      </p>
      {!target && (
      <div>
        <label htmlFor={`dispatch-to-${id}`}>Send to (optional — overrides the contractor list)</label>
        <input
          id={`dispatch-to-${id}`}
          type="email"
          value={toEmail}
          onChange={(e) => setToEmail(e.target.value)}
          placeholder={hasDefault ? defaults[0] : 'contractor@example.com'}
        />
      </div>
      )}
      <div>
        <label htmlFor={`dispatch-note-${id}`}>Note to the contractor (optional)</label>
        <textarea
          id={`dispatch-note-${id}`}
          value={note}
          rows={2}
          maxLength={500}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Anything the contractor should know before heading out."
        />
      </div>
      {canAssign && (
        <div className="row">
          <input
            id={`dispatch-assign-${id}`}
            type="checkbox"
            style={{ width: 'auto' }}
            checked={markAssigned}
            onChange={(e) => setMarkAssigned(e.target.checked)}
          />
          <label htmlFor={`dispatch-assign-${id}`} style={{ margin: 0 }}>
            Also mark this order assigned to the contractor
            {target ? ' (gives them access to the job in their app)' : ''}
          </label>
        </div>
      )}
      <div>
        <button type="submit" disabled={m.isPending || !canSend}>
          {m.isPending ? 'Sending…' : 'Email contractor'}
        </button>
      </div>
    </form>
  );
}

/**
 * Assign a directory contractor (0.25.0) — gives THAT contractor access to this job in their app
 * (and emails them). On a triaged/assigned order it is the assign transition; on a later status it
 * swaps the contractor without changing status. "Unassign" revokes their access immediately.
 */
/** 409 `stale_work_order`: someone changed this job (e.g. its contractor) since the page loaded. */
export function isStaleWorkOrder(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.body?.error === 'stale_work_order';
}

export const STALE_ASSIGNMENT_MESSAGE =
  'Someone else changed this job’s contractor in the meantime. The page has been refreshed: check the assignment and try again.';

export function ContractorAssignAction({ wo, canAssignTransition }: { wo: WorkOrderDTO; canAssignTransition: boolean }) {
  const invalidate = useInvalidateWorkOrder(wo.id);
  // 0.28.0 review - on a 409 the view is stale: refetch it, so the page shows the real assignment.
  const onError = (err: unknown) => {
    if (isStaleWorkOrder(err)) invalidate();
  };
  const directory = useQuery({ queryKey: CONTRACTORS_KEY, queryFn: () => api.contractors.list() });
  const [contractorId, setContractorId] = useState('');
  const [note, setNote] = useState('');
  const active = (directory.data?.contractors ?? []).filter((c) => c.active);
  const current = (directory.data?.contractors ?? []).find((c) => c.id === wo.assignedContractorId);

  const assign = useMutation({
    mutationFn: () =>
      canAssignTransition
        ? api.workOrders.assign(wo.id, { contractorId, note: opt(note) ?? null })
        : api.workOrders.setContractor(wo.id, { contractorId, note: opt(note) ?? null }),
    onSuccess: () => {
      setNote('');
      setContractorId('');
      invalidate();
    },
    onError,
  });
  const unassign = useMutation({
    mutationFn: () => api.workOrders.setContractor(wo.id, { contractorId: null }),
    onSuccess: invalidate,
    onError,
  });
  const failure = assign.error ?? unassign.error;

  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (contractorId) assign.mutate();
      }}
    >
      <h4 style={{ margin: 0 }}>Assign contractor</h4>
      {(assign.isError || unassign.isError) && (
        <StatusMessage tone="err">{isStaleWorkOrder(failure) ? STALE_ASSIGNMENT_MESSAGE : errorMessage(failure)}</StatusMessage>
      )}
      {wo.assignedContractorId && (
        <div className="row">
          <span>
            Assigned to <strong>{current?.companyName ?? wo.assignedTo ?? 'a contractor'}</strong> — they can see this
            job and the tenant’s contact details.
          </span>
          <button type="button" className="secondary" disabled={unassign.isPending} onClick={() => unassign.mutate()}>
            {unassign.isPending ? 'Removing…' : 'Unassign'}
          </button>
        </div>
      )}
      {active.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          No contractors yet — add one on the Contractors page.
        </p>
      ) : (
        <>
          <div>
            <label htmlFor={`assign-ctr-${wo.id}`}>{wo.assignedContractorId ? 'Reassign to' : 'Contractor'}</label>
            <select id={`assign-ctr-${wo.id}`} value={contractorId} onChange={(e) => setContractorId(e.target.value)}>
              <option value="">Choose a contractor…</option>
              {active
                .filter((c) => c.id !== wo.assignedContractorId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.companyName}
                    {c.trade ? ` — ${c.trade}` : ''}
                    {c.userId ? '' : ' (not invited yet)'}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label htmlFor={`assign-ctr-note-${wo.id}`}>Note to the contractor (optional)</label>
            <input id={`assign-ctr-note-${wo.id}`} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div>
            <button type="submit" disabled={assign.isPending || !contractorId}>
              {assign.isPending ? 'Assigning…' : 'Assign contractor'}
            </button>
          </div>
        </>
      )}
    </form>
  );
}

/**
 * Contractor cost review rows (0.25.0). Approve/Reject are OWNER-only (the API enforces it with
 * MFA; staff see the list with the buttons hidden). Approving recomputes the job's cost as the sum
 * of its approved contractor costs (that total feeds the tax export).
 */
function CostReviewList({ costs, showJob, onChanged }: { costs: WorkOrderCostReviewDTO[]; showJob: boolean; onChanged: () => void }) {
  const { principal } = useAuth();
  const isOwner = principal?.role === 'owner';
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const act = async (cost: WorkOrderCostReviewDTO, decision: 'approve' | 'reject') => {
    let reviewNote: string | null = null;
    if (decision === 'reject') {
      const n = window.prompt('Reason for rejecting (sent to the contractor, optional):');
      if (n === null) return;
      reviewNote = n.trim() || null;
    }
    setErr(null);
    setBusy(cost.id);
    try {
      // The DISPLAYED amount is part of the server's compare-and-set: if the contractor edited the
      // cost since this list loaded, the review is refused (409 cost_changed) and the list reloads.
      const input = { expectedAmountCents: cost.amountCents, expectedUpdatedAt: cost.updatedAt, reviewNote };
      if (decision === 'approve') await api.workOrderCosts.approve(cost.id, input);
      else await api.workOrderCosts.reject(cost.id, input);
      onChanged();
    } catch (e) {
      const changed = e instanceof ApiError && e.body?.error === 'cost_changed';
      setErr(changed ? 'The contractor changed this cost after you loaded it — review the new amount.' : errorMessage(e));
      if (changed) onChanged();
    } finally {
      setBusy(null);
    }
  };

  const openInvoice = async (documentId: string) => {
    try {
      const { url } = await api.documents.signedUrl(documentId, {});
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setErr(errorMessage(e));
    }
  };

  return (
    <div className="stack">
      {err && <StatusMessage tone="err">{err}</StatusMessage>}
      {!isOwner && costs.some((c) => c.status === 'submitted') && (
        <p className="muted" style={{ margin: 0 }}>
          Only an owner can approve or reject contractor costs.
        </p>
      )}
      <div className="table-wrap">
        <table>
          <caption className="visually-hidden">Contractor costs</caption>
          <thead>
            <tr>
              {showJob && <th scope="col">Job</th>}
              <th scope="col">Contractor</th>
              <th scope="col">Amount</th>
              <th scope="col">Description</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {costs.map((c) => (
              <tr key={c.id}>
                {showJob && <td>{c.workOrderTitle}</td>}
                <td>{c.contractorCompanyName}</td>
                <td>
                  {formatCents(c.amountCents)}
                  {c.updatedAt > c.createdAt && c.status === 'submitted' ? (
                    <div>
                      <span className="badge badge--warn">edited after submission</span>
                    </div>
                  ) : null}
                </td>
                <td>
                  {c.description}
                  {c.reviewNote ? <div className="muted">Note: {c.reviewNote}</div> : null}
                </td>
                <td>
                  <span className="badge">{c.status === 'submitted' ? 'pending' : c.status}</span>
                </td>
                <td>
                  <div className="row">
                    {c.invoiceDocumentId && (
                      <button type="button" className="secondary" onClick={() => void openInvoice(c.invoiceDocumentId!)}>
                        Invoice
                      </button>
                    )}
                    {isOwner && c.status === 'submitted' && (
                      <>
                        <button type="button" disabled={busy === c.id} onClick={() => void act(c, 'approve')}>
                          Approve<span className="visually-hidden"> {formatCents(c.amountCents)}</span>
                        </button>
                        <button type="button" className="secondary" disabled={busy === c.id} onClick={() => void act(c, 'reject')}>
                          Reject<span className="visually-hidden"> {formatCents(c.amountCents)}</span>
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Per-job contractor costs (detail view). */
function WorkOrderCosts({ id }: { id: string }) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: costsKey(id), queryFn: () => api.workOrders.costs(id) });
  if (query.isLoading || !query.data || query.data.costs.length === 0) return null;
  return (
    <section className="stack" aria-label="Contractor costs">
      <h4 style={{ margin: 0 }}>Contractor costs</h4>
      <CostReviewList
        costs={query.data.costs}
        showJob={false}
        onChanged={() => {
          void qc.invalidateQueries({ queryKey: costsKey(id) });
          void qc.invalidateQueries({ queryKey: PENDING_COSTS_KEY });
          void qc.invalidateQueries({ queryKey: detailKey(id) });
          void qc.invalidateQueries({ queryKey: WORK_ORDERS_KEY });
        }}
      />
    </section>
  );
}

/**
 * Contractor entry-window proposals (0.25.0). Confirm serves the §1954 notice + schedules the job
 * as the owner; OWNER-only (staff see the list, buttons hidden — the API also refuses them).
 */
function ProposalReviewList({ proposals, showJob, onChanged }: { proposals: ScheduleProposalReviewDTO[]; showJob: boolean; onChanged: () => void }) {
  const { principal } = useAuth();
  const isOwner = principal?.role === 'owner';
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const act = async (p: ScheduleProposalReviewDTO, decision: 'confirm' | 'decline') => {
    let reviewNote: string | null = null;
    if (decision === 'decline') {
      const n = window.prompt('Reason / a better time (sent to the contractor, optional):');
      if (n === null) return;
      reviewNote = n.trim() || null;
    }
    setErr(null);
    setBusy(p.id);
    try {
      if (decision === 'confirm') await api.scheduleProposals.confirm(p.id, {});
      else await api.scheduleProposals.decline(p.id, { reviewNote });
      onChanged();
    } catch (e) {
      const code = e instanceof ApiError ? e.body?.error : undefined;
      setErr(
        code === 'proposal_too_late'
          ? 'This window now starts in less than 24 hours, so the tenant can’t get legal notice. Decline it and ask for another time.'
          : code === 'proposal_already_reviewed' || code === 'proposal_not_open'
            ? 'This proposal was already handled or replaced — refreshing.'
            : code === 'entry_notice_serve_failed'
              ? 'Confirmed and scheduled, but the entry notice could not be sent. Open the job and choose “Send entry notice”.'
            : code === 'proposal_stale'
              ? 'The job was reassigned or closed since this was proposed.'
              : errorMessage(e),
      );
      onChanged();
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="stack">
      {err && <StatusMessage tone="err">{err}</StatusMessage>}
      {!isOwner && proposals.some((p) => p.status === 'proposed') && (
        <p className="muted" style={{ margin: 0 }}>
          Only an owner can confirm a contractor’s proposed time (confirming schedules the visit and serves the
          tenant the entry notice).
        </p>
      )}
      <div className="table-wrap">
        <table>
          <caption className="visually-hidden">Proposed entry windows</caption>
          <thead>
            <tr>
              {showJob && <th scope="col">Job</th>}
              <th scope="col">Contractor</th>
              <th scope="col">Window</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {proposals.map((p) => (
              <tr key={p.id}>
                {showJob && <td>{p.workOrderTitle}</td>}
                <td>{p.contractorCompanyName}</td>
                <td>
                  {formatDateTime(p.windowStart)} – {formatDateTime(p.windowEnd)}
                  {p.note ? <div className="muted">Note: {p.note}</div> : null}
                  {p.reviewNote ? <div className="muted">Reply: {p.reviewNote}</div> : null}
                </td>
                <td>
                  <span className="badge">{p.status}</span>
                </td>
                <td>
                  {isOwner && p.status === 'proposed' && (
                    <div className="row">
                      <button type="button" disabled={busy === p.id} onClick={() => void act(p, 'confirm')}>
                        Confirm &amp; serve entry notice<span className="visually-hidden"> {p.workOrderTitle}</span>
                      </button>
                      <button type="button" className="secondary" disabled={busy === p.id} onClick={() => void act(p, 'decline')}>
                        Decline<span className="visually-hidden"> {p.workOrderTitle}</span>
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Per-job proposals (detail view). */
function WorkOrderProposals({ id }: { id: string }) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: proposalsKey(id), queryFn: () => api.scheduleProposals.list({ status: 'all', workOrderId: id }) });
  if (!query.data || query.data.proposals.length === 0) return null;
  return (
    <section className="stack" aria-label="Proposed entry windows">
      <h4 style={{ margin: 0 }}>Contractor-proposed times</h4>
      <ProposalReviewList
        proposals={query.data.proposals}
        showJob={false}
        onChanged={() => {
          void qc.invalidateQueries({ queryKey: proposalsKey(id) });
          void qc.invalidateQueries({ queryKey: PENDING_PROPOSALS_KEY });
          void qc.invalidateQueries({ queryKey: detailKey(id) });
          void qc.invalidateQueries({ queryKey: WORK_ORDERS_KEY });
        }}
      />
    </section>
  );
}

/** Queue-level "Proposed times" panel (hidden when nothing is pending). */
function PendingProposals() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: PENDING_PROPOSALS_KEY, queryFn: () => api.scheduleProposals.list({ status: 'proposed' }) });
  if (!query.data || query.data.proposals.length === 0) return null;
  return (
    <section className="card stack" aria-labelledby="pending-proposals-h">
      <h2 id="pending-proposals-h" style={{ margin: 0 }}>
        Proposed entry times ({query.data.proposals.length})
      </h2>
      <ProposalReviewList
        proposals={query.data.proposals}
        showJob
        onChanged={() => {
          void qc.invalidateQueries({ queryKey: PENDING_PROPOSALS_KEY });
          void qc.invalidateQueries({ queryKey: WORK_ORDERS_KEY });
        }}
      />
    </section>
  );
}

/**
 * What really happened to ONE recipient's EMAIL COPY of an entry notice (0.28.0) — "sent" only when
 * the email provider accepted it; a failed, pending or never-sent copy says so.
 */
function noticeCopyLabel(r: EntryNoticeEmailRecipientDTO): string {
  const who = r.name || 'Tenant';
  switch (r.status) {
    case 'sent':
      return `${who}: sent to ${r.to ?? 'their address'}${r.sentAt ? ` · ${formatDateTime(r.sentAt)}` : ''}`;
    case 'pending':
      return `${who}: queued for ${r.to ?? 'their address'}${r.detail ? ` — ${r.detail}` : ''}`;
    case 'failed':
      return `${who}: FAILED to ${r.to ?? 'their address'}${r.detail ? ` — ${r.detail}` : ''}`;
    default:
      return `${r.tenantId ? who : 'No tenant on file'}: not sent${r.detail ? ` — ${r.detail}` : ''}`;
  }
}

/** The email copies of one notice: a line per recipient, or the notice-level note when there is none. */
function NoticeEmailCopies({ notice }: { notice: EntryNoticeDTO }) {
  if (notice.emailRecipients.length === 0) {
    return <strong>Not sent{notice.emailDetail ? ` — ${notice.emailDetail}` : ''}</strong>;
  }
  return (
    <>
      {notice.emailRecipients.map((r, i) => (
        <div key={r.tenantId ?? `none-${i}`}>
          {r.status === 'failed' || r.status === 'not_sent' ? <strong>{noticeCopyLabel(r)}</strong> : noticeCopyLabel(r)}
        </div>
      ))}
    </>
  );
}

/** Re-serve the §1954 notice for a scheduled job whose notice failed to send (idempotent). */
function ServeNoticeAction({ id }: { id: string }) {
  const invalidate = useInvalidateWorkOrder(id);
  const m = useMutation({ mutationFn: () => api.workOrders.serveEntryNotice(id), onSuccess: invalidate });
  return (
    <div className="card stack" role="group" aria-label="Entry notice not sent">
      <StatusMessage tone="warn">This job is scheduled but its entry notice has not been sent to the tenant.</StatusMessage>
      {m.isError && <StatusMessage tone="err">{errorMessage(m.error)}</StatusMessage>}
      <div>
        <button type="button" disabled={m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? 'Sending…' : 'Send entry notice'}
        </button>
      </div>
    </div>
  );
}

/** Manual (owner/staff) part of the job cost; the total adds approved contractor costs. */
function ManualCostAction({ wo }: { wo: WorkOrderDTO }) {
  const invalidate = useInvalidateWorkOrder(wo.id);
  const [v, setV] = useState(wo.manualCostCents != null ? (wo.manualCostCents / 100).toFixed(2) : '');
  const m = useMutation({
    mutationFn: () => api.workOrders.setManualCost(wo.id, { manualCostCents: v.trim() === '' ? null : dollarsToCents(v) }),
    onSuccess: invalidate,
  });
  return (
    <form
      className="row"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <label htmlFor={`manual-cost-${wo.id}`} style={{ margin: 0 }}>
        Your cost (USD)
      </label>
      <input
        id={`manual-cost-${wo.id}`}
        type="number"
        min="0"
        step="0.01"
        style={{ width: '8rem' }}
        value={v}
        onChange={(e) => setV(e.target.value)}
      />
      <button type="submit" className="secondary" disabled={m.isPending}>
        {m.isPending ? 'Saving…' : 'Save cost'}
      </button>
      {m.isError && <span role="alert">{errorMessage(m.error)}</span>}
    </form>
  );
}

/** Queue-level "Costs pending approval" panel (hidden when nothing is pending). */
function PendingCosts() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: PENDING_COSTS_KEY, queryFn: () => api.workOrderCosts.list({ status: 'submitted' }) });
  if (!query.data || query.data.costs.length === 0) return null;
  return (
    <section className="card stack" aria-labelledby="pending-costs-h">
      <h2 id="pending-costs-h" style={{ margin: 0 }}>
        Costs pending approval ({query.data.costs.length})
      </h2>
      <CostReviewList
        costs={query.data.costs}
        showJob
        onChanged={() => {
          void qc.invalidateQueries({ queryKey: PENDING_COSTS_KEY });
          void qc.invalidateQueries({ queryKey: WORK_ORDERS_KEY });
        }}
      />
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Contractor settings — the org's one primary maintenance contractor. *
 * ------------------------------------------------------------------ */

function ContractorSettings({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const contractor = useQuery({ queryKey: CONTRACTOR_KEY, queryFn: () => api.org.getContractor() });
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [emailsText, setEmailsText] = useState('');
  const [seeded, setSeeded] = useState(false);

  // Seed the form from the current contractor ONCE — so a background refetch (e.g. window refocus)
  // can't overwrite the owner's in-progress edits with the last-fetched server value.
  useEffect(() => {
    if (contractor.data && !seeded) {
      setName(contractor.data.name ?? '');
      setPhone(contractor.data.phone ?? '');
      setEmailsText(contractor.data.emails.join('\n'));
      setSeeded(true);
    }
  }, [contractor.data, seeded]);

  const save = useMutation({
    mutationFn: () => {
      // One address per line (also tolerant of comma/semicolon separators).
      const emails = emailsText
        .split(/[\n,;]+/)
        .map((e) => e.trim())
        .filter((e) => e.length > 0);
      const input: ContractorUpdateInput = { name: opt(name) ?? null, emails, phone: opt(phone) ?? null };
      return api.org.updateContractor(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CONTRACTOR_KEY });
      onClose();
    },
  });

  return (
    <Dialog title="Maintenance contractor" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        {contractor.isLoading ? (
          <Loading label="Loading contractor…" />
        ) : (
          <>
            <p className="muted" style={{ margin: 0 }}>
              Your primary maintenance contractor. Dispatching a work order emails the job details to
              every address below.
            </p>
            <div>
              <label htmlFor="contractor-name">Name (optional)</label>
              <input
                id="contractor-name"
                value={name}
                maxLength={200}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Allen Porter"
              />
            </div>
            <div>
              <label htmlFor="contractor-emails">Email addresses (one per line)</label>
              <textarea
                id="contractor-emails"
                value={emailsText}
                rows={3}
                onChange={(e) => setEmailsText(e.target.value)}
                placeholder={'allen@allenportercorp.com\nallenportercorp@gmail.com'}
              />
            </div>
            <div>
              <label htmlFor="contractor-phone">Phone (optional)</label>
              <input
                id="contractor-phone"
                value={phone}
                maxLength={40}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(805) 555-0100"
              />
            </div>
            <div className="row">
              <button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save contractor'}
              </button>
              <button type="button" className="secondary" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Row detail — meta, actions, entry notices, and history.            *
 * ------------------------------------------------------------------ */

function WorkOrderDetail({ id }: { id: string }) {
  const query = useQuery({ queryKey: detailKey(id), queryFn: () => api.workOrders.get(id) });
  const { principal } = useAuth();
  const isOwner = principal?.role === 'owner';

  if (query.isLoading) return <Loading label="Loading work order…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;

  const { workOrder: wo, history, entryNotices } = query.data;
  // The newest notice must cover the scheduled window; otherwise the notice failed to serve.
  const latestNotice = entryNotices[0];
  const noticeCoversSchedule = Boolean(
    latestNotice &&
      latestNotice.entryStartAt === wo.scheduledEntryStart &&
      (latestNotice.entryEndAt ?? null) === (wo.scheduledEntryEnd ?? null),
  );
  const status = wo.status;
  const terminal = status === 'closed' || status === 'cancelled';

  const canTriage = status === 'submitted';
  const canAssign = status === 'triaged' || status === 'assigned';
  const canSchedule = status === 'triaged' || status === 'assigned';
  const canResolve = status === 'scheduled';
  const canClose = status === 'resolved';
  const canCancel =
    status === 'submitted' ||
    status === 'triaged' ||
    status === 'assigned' ||
    status === 'scheduled';

  return (
    <div className="stack" style={{ padding: '0.75rem' }}>
      <div>
        <p style={{ margin: '0 0 0.25rem' }}>{wo.description}</p>
        <p className="muted" style={{ margin: 0 }}>
          Priority: {wo.priority} · Permission to enter: {wo.permissionToEnter ? 'yes' : 'no'}
          {wo.availability ? ` · Availability: ${wo.availability}` : ''}
          {wo.assignedTo ? ` · Assigned to: ${wo.assignedTo}` : ''}
        </p>
        {wo.dispatchedAt && (
          <p className="muted" style={{ margin: 0 }}>
            Dispatched to contractor {formatDateTime(wo.dispatchedAt)}
            {wo.dispatchedToEmail ? ` · ${wo.dispatchedToEmail}` : ''}
          </p>
        )}
        {wo.scheduledEntryStart && (
          <p className="muted" style={{ margin: 0 }}>
            Scheduled entry: {formatDateTime(wo.scheduledEntryStart)}
            {wo.scheduledEntryEnd ? ` – ${formatDateTime(wo.scheduledEntryEnd)}` : ''}
          </p>
        )}
        {wo.resolvedAt && (
          <p className="muted" style={{ margin: 0 }}>
            Resolved {formatDateTime(wo.resolvedAt)}
            {wo.resolutionNote ? ` · ${wo.resolutionNote}` : ''}
          </p>
        )}
        <p className="muted" style={{ margin: 0 }}>
          Job cost: {wo.resolutionCostCents != null ? formatCents(wo.resolutionCostCents) : '—'}
          {wo.resolutionCostCents != null
            ? ` (your cost ${formatCents(wo.manualCostCents ?? 0)} + approved contractor costs ${formatCents(wo.approvedContractorCostCents ?? 0)})`
            : ''}
        </p>
        <ManualCostAction key={`${wo.id}:${wo.manualCostCents ?? ''}`} wo={wo} />
        {wo.cancelledReason && (
          <p className="muted" style={{ margin: 0 }}>
            Cancelled: {wo.cancelledReason}
          </p>
        )}
      </div>

      {!terminal && (
        <section aria-label="Actions" className="stack">
          {canTriage && <TriageAction id={id} />}
          {/* A free-text assignee would remove an app contractor's access → owner-only then. */}
          {canAssign && (isOwner || !wo.assignedContractorId) && <AssignAction id={id} />}
          {canSchedule && <ScheduleAction id={id} />}
          {canResolve && <ResolveAction id={id} />}
          {canClose && <CloseAction id={id} />}
          {/* Granting a vendor access / emailing them the job is OWNER-only (API enforces too). */}
          {isOwner && <ContractorAssignAction wo={wo} canAssignTransition={canAssign} />}
          {isOwner && <DispatchAction id={id} canAssign={canAssign} />}
          {canCancel && <CancelAction id={id} />}
          <NoteAction id={id} />
        </section>
      )}

      {status === 'scheduled' && wo.scheduledEntryStart && !noticeCoversSchedule && <ServeNoticeAction id={id} />}
      <WorkOrderProposals id={id} />
      <WorkOrderCosts id={id} />

      {entryNotices.length > 0 && (
        <div className="table-wrap">
          <table>
            <caption className="visually-hidden">Entry notices</caption>
            <thead>
              <tr>
                <th scope="col">Purpose</th>
                <th scope="col">Entry start</th>
                <th scope="col">Short notice</th>
                <th scope="col">Served (portal)</th>
                <th scope="col">Email copies</th>
              </tr>
            </thead>
            <tbody>
              {entryNotices.map((n) => (
                <tr key={n.id}>
                  <td>{n.purpose}</td>
                  <td>{formatDateTime(n.entryStartAt)}</td>
                  <td>{n.overrideShortNotice ? `yes${n.shortNoticeReason ? ` (${n.shortNoticeReason})` : ''}` : 'no'}</td>
                  <td>{n.servedAt ? formatDateTime(n.servedAt) : '—'}</td>
                  <td>
                    <NoticeEmailCopies notice={n} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {history.length > 0 && (
        <div className="table-wrap">
          <table>
            <caption className="visually-hidden">Status history</caption>
            <thead>
              <tr>
                <th scope="col">Status</th>
                <th scope="col">Note</th>
                <th scope="col">When</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td>
                    <span className="badge">{h.toStatus}</span>
                  </td>
                  <td>{h.note || '—'}</td>
                  <td>{formatDateTime(h.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Tax export — completed work + cost as an .xlsx download.            *
 * ------------------------------------------------------------------ */

function ExportButton() {
  const thisYear = new Date().getFullYear();
  const years = [thisYear, thisYear - 1, thisYear - 2, thisYear - 3];
  const [year, setYear] = useState<string>('all');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setErr(null);
    try {
      const blob = await api.workOrders.exportXlsx(year === 'all' ? {} : { year: Number(year) });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `work-orders-${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="row" style={{ alignItems: 'center', gap: '0.5rem' }}>
      <label htmlFor="wo-export-year" className="visually-hidden">
        Tax year
      </label>
      <select id="wo-export-year" value={year} onChange={(e) => setYear(e.target.value)}>
        <option value="all">All time</option>
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
      <button type="button" className="secondary" disabled={busy} onClick={() => void download()}>
        {busy ? 'Exporting…' : 'Export for taxes'}
      </button>
      {err && (
        <span className="muted" role="alert">
          {err}
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The queue.                                                          *
 * ------------------------------------------------------------------ */

export function AdminWorkOrders() {
  const query = useQuery({ queryKey: WORK_ORDERS_KEY, queryFn: () => api.workOrders.list() });
  const { principal } = useAuth();
  const [creating, setCreating] = useState(false);
  const [editingContractor, setEditingContractor] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  if (query.isLoading) return <Loading label="Loading work orders…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;

  const workOrders: WorkOrderDTO[] = query.data.workOrders;

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Work orders</h1>
        <div className="row" style={{ alignItems: 'center' }}>
          <ExportButton />
          {principal?.role === 'owner' && (
            <button type="button" className="secondary" onClick={() => setEditingContractor(true)}>
              Contractor
            </button>
          )}
          <button type="button" onClick={() => setCreating(true)}>
            New work order
          </button>
        </div>
      </div>
      <p className="subtitle">Maintenance requests across the portfolio. Expand a row to triage, assign, schedule entry, resolve, or close.</p>

      <PendingProposals />
      <PendingCosts />

      {workOrders.length === 0 ? (
        <p className="muted">No work orders yet. Choose “New work order” to raise one.</p>
      ) : (
        <div className="table-wrap card" style={{ padding: 0 }}>
          <table>
            <caption className="visually-hidden">Work orders</caption>
            <thead>
              <tr>
                <th scope="col">Title</th>
                <th scope="col">Category</th>
                <th scope="col">Priority</th>
                <th scope="col">Status</th>
                <th scope="col">Unit</th>
                <th scope="col">Created</th>
                <th scope="col">Details</th>
              </tr>
            </thead>
            <tbody>
              {workOrders.map((w) => (
                <Fragment key={w.id}>
                  <tr>
                    <td>{w.title}</td>
                    <td>{w.category}</td>
                    <td>{w.priority}</td>
                    <td>
                      <span className="badge">{w.status}</span>
                    </td>
                    <td>{w.unitId}</td>
                    <td>{formatDate(w.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="secondary"
                        aria-expanded={expanded === w.id}
                        onClick={() => setExpanded(expanded === w.id ? null : w.id)}
                      >
                        {expanded === w.id ? 'Hide' : 'Details'}
                        <span className="visually-hidden"> for {w.title}</span>
                      </button>
                    </td>
                  </tr>
                  {expanded === w.id && (
                    <tr>
                      <td colSpan={7} style={{ padding: 0 }}>
                        <WorkOrderDetail id={w.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <WorkOrderForm onClose={() => setCreating(false)} />}
      {editingContractor && <ContractorSettings onClose={() => setEditingContractor(false)} />}
    </>
  );
}
